'use client'

import { MessageSquarePlusIcon, SendIcon } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Textarea } from '~/components/ui/textarea'
import { env } from '~/env'
import { useSession } from '~/lib/auth.client'
import { cn } from '~/lib/utils'

const feedbackCategories = [
  { value: 'bug', label: 'Ошибка' },
  { value: 'improvement', label: 'Улучшение' },
  { value: 'question', label: 'Вопрос по контенту' },
  { value: 'feature', label: 'Новая функция' },
  { value: 'other', label: 'Другое' },
] as const

const MAX_CONSOLE_LOGS = 100

interface ConsoleLogEntry {
  type: 'log' | 'warn' | 'error' | 'info'
  timestamp: string
  args: string
}

function useConsoleLogs(): ConsoleLogEntry[] {
  const logsRef = useRef<ConsoleLogEntry[]>([])
  const [logs, setLogs] = useState<ConsoleLogEntry[]>([])

  useEffect(() => {
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
    }

    function createInterceptor(type: ConsoleLogEntry['type']) {
      return (...args: unknown[]) => {
        const entry: ConsoleLogEntry = {
          type,
          timestamp: new Date().toISOString(),
          args: args.map((arg) => {
            try {
              return typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
            }
            catch {
              return '[Unserializable]'
            }
          }).join(' '),
        }

        logsRef.current = [...logsRef.current.slice(-MAX_CONSOLE_LOGS + 1), entry]
        setLogs(logsRef.current)

        originalConsole[type](...args)
      }
    }

    console.log = createInterceptor('log')
    console.warn = createInterceptor('warn')
    console.error = createInterceptor('error')
    console.info = createInterceptor('info')

    return () => {
      console.log = originalConsole.log
      console.warn = originalConsole.warn
      console.error = originalConsole.error
      console.info = originalConsole.info
    }
  }, [])

  return logs
}

export function FeedbackButton() {
  const { data: session, isPending } = useSession()
  const pathname = usePathname()
  const consoleLogs = useConsoleLogs()

  const [isOpen, setIsOpen] = useState(false)
  const [category, setCategory] = useState<string>('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isDev = env.NEXT_PUBLIC_NODE_ENV === 'development'

  // Only render for authenticated users (always visible in dev mode)
  if (!isDev && (isPending || !session?.user)) {
    return null
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : pathname

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!category || !message.trim()) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category,
          message: message.trim(),
          pageUrl,
          consoleLogs,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to submit feedback')
      }

      setCategory('')
      setMessage('')
      setIsOpen(false)
      toast.success('Спасибо за обратную связь!', {
        description: 'Мы обязательно рассмотрим ваше сообщение',
      })
    }
    catch (error) {
      console.error('Error submitting feedback:', error)
      toast.error('Не удалось отправить сообщение', {
        description: 'Попробуйте ещё раз позже',
      })
    }
    finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = category !== '' && message.trim().length > 0

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          className={cn(
            'fixed right-6 bottom-6 z-50',
            'gap-2.5 rounded-full px-5 shadow-lg',
            'bg-gradient-to-r from-violet-600 to-indigo-600 opacity-25',
            'hover:from-violet-500 hover:to-indigo-500 hover:opacity-100',
            'hover:shadow-xl hover:shadow-violet-500/25',
            'transition-all duration-300',
            'border border-white/10',
          )}
        >
          <MessageSquarePlusIcon className="size-5" />
          <span className="font-medium max-sm:hidden">Оставить отзыв</span>
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Обратная связь
          </DialogTitle>
          <DialogDescription>
            Помогите нам стать лучше - расскажите о проблемах или предложите улучшения
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="feedback-category">Категория</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="feedback-category" className="w-full">
                <SelectValue placeholder="Выберите категорию" />
              </SelectTrigger>
              <SelectContent>
                {feedbackCategories.map(item => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="feedback-message">Сообщение</Label>
            <Textarea
              id="feedback-message"
              name="message"
              placeholder="Опишите вашу проблему или предложение..."
              value={message}
              onChange={event => setMessage(event.target.value)}
              rows={5}
            />
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={!isFormValid}
              isLoading={isSubmitting}
              className="w-full"
              size="lg"
            >
              <SendIcon className="size-4" />
              Отправить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
